/**
 * Self-contained HTML export, PDF and print. Ported from the desktop's
 * `main/export/html.ts`, `pdf.ts` and `print.ts`.
 *
 * "Self-contained" is the point: the exported file is something sent to
 * someone, so stylesheets are inlined and the document's own images embedded as
 * data URIs. Remote images are left as links: fetching them would make the
 * export reach out to the network, which an export of a local document has no
 * business doing.
 *
 * PDF and print share one path, a hidden iframe printed with the browser's own
 * dialog, laid out by `@page` rules from the page setup. A browser cannot write
 * a PDF on its own, so Export PDF says to choose "Save as PDF" there.
 */
import type { ExportPayload, ExportResult } from './types'
import { MARGINS, validPageSetup, type PageSetup } from '../shared/page-setup'
import { blobFor } from './images'
import { download } from './files'
import { hasFileSystemAccess, toPlatformError } from './handles'

/** Images above this are left out: a data URI of one is unusable anyway. */
const MAX_INLINE_BYTES = 10 * 1024 * 1024

/**
 * The image type a file's own first bytes declare, or null for anything else.
 * Decided by content, never by the link's extension: whatever is embedded
 * travels inside an export the user then shares.
 */
export function imageType(data: Uint8Array): string | null {
  const b = data.subarray(0, 16)
  const png = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a]
  if (png.every((x, i) => b[i] === x)) return 'image/png'
  if (b[0] === 0xff && b[1] === 0xd8 && b[2] === 0xff) return 'image/jpeg'
  const ascii = String.fromCharCode(...b)
  if (ascii.startsWith('GIF87a') || ascii.startsWith('GIF89a')) return 'image/gif'
  if (ascii.startsWith('RIFF') && ascii.slice(8, 12) === 'WEBP') return 'image/webp'
  if (ascii.startsWith('BM')) return 'image/bmp'
  if (ascii.slice(4, 12) === 'ftypavif' || ascii.slice(4, 12) === 'ftypavis') return 'image/avif'
  // TextDecoder drops a leading byte order mark itself, so none is left to strip.
  const text = new TextDecoder().decode(data.subarray(0, 1024)).trimStart()
  if (/^(<\?xml[^>]*\?>\s*)?(<!--[\s\S]*?-->\s*)*<svg[\s>]/i.test(text)) return 'image/svg+xml'
  return null
}

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

function base64(bytes: Uint8Array): string {
  let s = ''
  for (let i = 0; i < bytes.length; i += 0x8000) {
    s += String.fromCharCode(...bytes.subarray(i, i + 0x8000))
  }
  return btoa(s)
}

/**
 * Replaces every blob: image source this page made with a data URI. Anything
 * unreadable keeps its source, which `stripLocalSources` then removes.
 */
export async function inlineImages(html: string): Promise<string> {
  const sources = new Map<string, string>()
  for (const match of html.matchAll(/src="(blob:[^"]+)"/g)) {
    const url = match[1]
    if (sources.has(url)) continue
    const blob = blobFor(url)
    if (!blob || blob.size > MAX_INLINE_BYTES) continue
    const bytes = new Uint8Array(await blob.arrayBuffer())
    const mime = imageType(bytes)
    if (mime) sources.set(url, `data:${mime};base64,${base64(bytes)}`)
  }
  let out = html
  for (const [url, dataUri] of sources) out = out.split(`src="${url}"`).join(`src="${dataUri}"`)
  return out
}

/**
 * Removes sources that point into this page: blob: URLs die with it and would
 * be broken images for the recipient. The image keeps its element and alt text.
 */
export function stripLocalSources(html: string): string {
  return html
    .replace(/\ssrc="blob:[^"]*"/gi, '')
    .replace(/<a\b[^>]*\shref="(?:blob|file):[^"]*"[^>]*>([\s\S]*?)<\/a>/gi, '$1')
}

/** Page layout for printing, from the page setup: paper, margins, page numbers. */
export function pageCss(setup: PageSetup): string {
  const m = MARGINS[setup.margin]
  const numbers = setup.pageNumbers
    ? `@bottom-center { content: counter(page) " / " counter(pages); font: 8pt sans-serif; color: #777; }`
    : ''
  return `@page { size: ${setup.pageSize} ${setup.landscape ? 'landscape' : 'portrait'}; margin: ${m}in; ${numbers} }`
}

/** Wraps the body in a complete document, styled by the app's own theme file. */
export function wrapDocument(payload: ExportPayload, body: string, extraCss = ''): string {
  return `<!doctype html>
<html lang="en" data-theme="${escapeHtml(payload.themeId)}">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<title>${escapeHtml(payload.title)}</title>
<style>
${payload.css}

/* Export-only: the application supplies this framing through its own chrome. */
body {
  margin: 0;
  background: var(--doc-bg);
  color: var(--doc-fg);
  font-family: var(--doc-font);
  font-size: var(--doc-font-size);
  line-height: var(--doc-line-height);
}
.export-document {
  max-width: var(--doc-measure);
  margin: 0 auto;
  padding: 48px 32px;
}
/* Printing: backgrounds are kept, and blocks are not split across pages. */
@media print {
  body { background: #fff; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  .export-document { max-width: none; padding: 0; }
  pre, blockquote, table, figure { break-inside: avoid; }
  h1, h2, h3, h4, h5, h6 { break-after: avoid; }
}
${extraCss}
</style>
</head>
<body>
<article class="export-document milkdown">
${body}
</article>
</body>
</html>
`
}

export async function buildHtml(payload: ExportPayload, extraCss = ''): Promise<string> {
  return wrapDocument(payload, stripLocalSources(await inlineImages(payload.bodyHtml)), extraCss)
}

/** Export HTML: the browser's save dialog where there is one, the downloads where not. */
export async function exportHtml(payload: ExportPayload): Promise<ExportResult> {
  const name = `${payload.title || 'document'}.html`
  try {
    const html = await buildHtml(payload)
    if (!hasFileSystemAccess) {
      download(name, html, 'text/html;charset=utf-8')
      return { ok: true, path: name, note: 'It is in your downloads.' }
    }
    let handle: FileSystemFileHandle
    try {
      handle = await window.showSaveFilePicker({
        suggestedName: name,
        types: [{ description: 'HTML', accept: { 'text/html': ['.html', '.htm'] } }],
      })
    } catch (err) {
      if ((err as DOMException)?.name === 'AbortError') return { ok: false, cancelled: true }
      throw err
    }
    const w = await handle.createWritable()
    await w.write(html)
    await w.close()
    return { ok: true, path: handle.name }
  } catch (err) {
    return { ok: false, error: toPlatformError(err, name).message.replace(/^E[A-Z]+: /, '') }
  }
}

/** The frame of the last print, kept until the next: see `printDocument`. */
let printFrame: HTMLIFrameElement | null = null

/**
 * Prints an export through a hidden iframe. Sandboxed with no scripts allowed:
 * nothing in an exported document runs. `allow-same-origin` lets this page call
 * its print(), `allow-modals` lets the print dialog open.
 *
 * No `afterprint` listener, and the frame is not removed afterwards. Scripts
 * are off in that frame, so a listener on its window never runs (the browser
 * only logs "Blocked script execution"). And while Chromium's print() returns
 * once its dialog closes, Firefox and Safari return at once and show the
 * preview later, which a removed frame would cut short. So the frame stays,
 * hidden, until the next print replaces it.
 */
function printDocument(html: string): Promise<void> {
  return new Promise((resolve, reject) => {
    printFrame?.remove()
    const frame = document.createElement('iframe')
    printFrame = frame
    frame.setAttribute('sandbox', 'allow-same-origin allow-modals')
    frame.setAttribute('aria-hidden', 'true')
    frame.tabIndex = -1
    frame.style.cssText =
      'position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden'
    frame.addEventListener('load', () => {
      const win = frame.contentWindow
      if (!win) {
        frame.remove()
        reject(new Error('The document could not be laid out for printing.'))
        return
      }
      // Fonts and layout settle a frame after load, as on the desktop. The
      // promise settles first and the dialog opens a moment after: print()
      // blocks the page in some browsers and not in others, and the message
      // saying what to do in the dialog must be on screen either way.
      setTimeout(() => {
        resolve()
        setTimeout(() => {
          try {
            win.focus()
            win.print()
          } catch {
            // Nothing to tell: the dialog did not open, and the user sees that.
          }
        }, 50)
      }, 250)
    })
    frame.srcdoc = html
    document.body.appendChild(frame)
  })
}

export async function printExport(
  payload: ExportPayload,
  rawSetup: unknown
): Promise<ExportResult> {
  try {
    await printDocument(await buildHtml(payload, pageCss(validPageSetup(rawSetup))))
    return { ok: true }
  } catch (err) {
    return { ok: false, error: err instanceof Error ? err.message : String(err) }
  }
}

export async function exportPdf(payload: ExportPayload, rawSetup: unknown): Promise<ExportResult> {
  const result = await printExport(payload, rawSetup)
  return result.ok
    ? { ok: true, note: 'To save a PDF, choose “Save as PDF” as the printer in the print dialog.' }
    : result
}
